import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil, isNotEmpty, isNotNil, trim} from 'ramda';

import i18n from '../../../../../i18n/i18n';
import {
  RelativeFilterChoice,
  RelativeFilterConfigBody,
  RelativeFilterResponseBody,
} from '../../../../../types/api/api';
import {getPresetDisplayName} from './getPresetDisplayName';
import {type IsKnownPresetFeature, normalizePresetFeatures} from './normalizePresetFeatures';

const intOrNull = z.union([
  z.null(),
  z.number().superRefine((val, ctx) => {
    if (!Number.isInteger(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.filterForm.savedFilters.dialog.integerRequired'),
      });
    }
  }),
]);

/** 100% cap: feature score preset offsets, and max vehicle − for mileage/power/price %. */
const MAX_PERCENT = 100;

const numericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable();

/**
 * Mileage / power / price %: `from` is 0 or in [−100, 0]; `to` is 0 or any positive % (vehicle +).
 */
const vehicleMetricPctNumericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable()
  .superRefine((bound, ctx) => {
    if (isNil(bound)) {
      return;
    }
    const signMessage = i18n.t('entity.filterForm.savedFilters.dialog.pctVehicleOffsetSignInvalid');
    const belowMaxMessage = i18n.t(
      'entity.filterForm.savedFilters.dialog.pctVehicleBelowPercentMax'
    );
    if (isNotNil(bound.from) && bound.from !== 0) {
      if (bound.from > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: signMessage,
          path: ['from'],
        });
      } else if (bound.from < -MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: belowMaxMessage,
          path: ['from'],
        });
      }
    }
    if (isNotNil(bound.to) && bound.to !== 0 && bound.to < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: signMessage,
        path: ['to'],
      });
    }
  });

/**
 * Feature score % offsets: magnitudes must stay within 0–100 from the vehicle level.
 */
const featureScorePctNumericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable()
  .superRefine((bound, ctx) => {
    if (isNil(bound)) {
      return;
    }
    const pctRangeMessage = i18n.t('entity.filterForm.savedFilters.dialog.pctOffsetOutOfRange');
    if (isNotNil(bound.from) && bound.from !== 0) {
      if (bound.from > 0 || bound.from < -MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: pctRangeMessage,
          path: ['from'],
        });
      }
    }
    if (isNotNil(bound.to) && bound.to !== 0) {
      if (bound.to < 0 || bound.to > MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: pctRangeMessage,
          path: ['to'],
        });
      }
    }
  });

const relativeFilterChoiceSchema = z.union([z.literal('same'), z.literal('same_better'), z.null()]);

const twoWayRelativeFilterChoiceSchema = z.union([z.literal('same'), z.null()]);

export const presetEditorFormSchema = z.object({
  name: z.preprocess(
    (value) => (value === null || value === undefined ? '' : value),
    z.string().superRefine((val, ctx) => {
      if (!isNotEmpty(trim(val))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.filterForm.savedFilters.dialog.presetNameRequired'),
        });
      }
    })
  ),
  year: numericBoundSchema,
  mileage: numericBoundSchema,
  mileagePct: vehicleMetricPctNumericBoundSchema,
  power: numericBoundSchema,
  powerPct: vehicleMetricPctNumericBoundSchema,
  price: numericBoundSchema,
  pricePct: vehicleMetricPctNumericBoundSchema,
  featureScore: featureScorePctNumericBoundSchema,
  trimLevel: relativeFilterChoiceSchema,
  transmission: relativeFilterChoiceSchema,
  fuel: twoWayRelativeFilterChoiceSchema,
  body: twoWayRelativeFilterChoiceSchema,
  drive: relativeFilterChoiceSchema,
  vatDeductible: relativeFilterChoiceSchema,
  sellerType: z.array(z.string()).nullable(),
  seats: relativeFilterChoiceSchema,
  features: z.union([z.null(), z.array(z.string())]),
  radius: z.union([
    z.null(),
    z.number().superRefine((val, ctx) => {
      if (!Number.isInteger(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.filterForm.savedFilters.dialog.integerRequired'),
        });
        return;
      }
      if (val < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.filterForm.error.radius.positive'),
        });
      }
    }),
  ]),
  isDefault: z.boolean(),
});

export type PresetEditorFormValues = z.infer<typeof presetEditorFormSchema>;

const EMPTY_CONFIG: RelativeFilterConfigBody = {
  year: null,
  mileage: null,
  mileagePct: null,
  power: null,
  powerPct: null,
  price: null,
  pricePct: null,
  featureScore: null,
  trimLevel: null,
  transmission: null,
  fuel: null,
  body: null,
  drive: null,
  vatDeductible: null,
  sellerType: null,
  seats: null,
  features: null,
  radius: null,
};

function twoWayRelativeChoiceFromApi(value: RelativeFilterChoice | null): 'same' | null {
  return match<RelativeFilterChoice | null, 'same' | null>(value)
    .with(null, () => null)
    .with('same', () => 'same')
    .with('same_better', () => 'same')
    .exhaustive();
}

/** Coerces legacy RelativeFilterChoice strings (`'same'` / `'same_better'`) to null. */
function sellerTypeFromApi(value: RelativeFilterConfigBody['sellerType']): string[] | null {
  return Array.isArray(value) ? value : null;
}

function normalizeConfigForPresetEditor(
  config: RelativeFilterConfigBody
): Omit<PresetEditorFormValues, 'name' | 'isDefault'> {
  return {
    ...config,
    fuel: twoWayRelativeChoiceFromApi(config.fuel),
    body: twoWayRelativeChoiceFromApi(config.body),
    sellerType: sellerTypeFromApi(config.sellerType),
    features: config.features ?? null,
  };
}

export function buildPresetEditorDefaultValues(
  initialPreset: RelativeFilterResponseBody | null
): PresetEditorFormValues {
  return {
    // Display name — for the system preset the (disabled) field shows the translation
    name: isNotNil(initialPreset) ? getPresetDisplayName(initialPreset) : '',
    isDefault: initialPreset?.default ?? false,
    ...normalizeConfigForPresetEditor(initialPreset?.config ?? EMPTY_CONFIG),
  };
}

export function getRelativeFilterConfigFromPresetEditorForm(
  values: PresetEditorFormValues,
  isKnownFeature: IsKnownPresetFeature = () => true
): RelativeFilterConfigBody {
  const {name: _name, isDefault: _isDefault, features, ...config} = values;

  return {
    ...config,
    features: normalizePresetFeatures(features, isKnownFeature),
  };
}
